import { ReactElement, useEffect, useState } from "react";
import React from "react";
import { TreeListReadDto } from "../../../Models/TreeList/TreeListReadDto";
import {
TreeList, 
filterBy,
mapTree,
extendDataItem,
getSelectedState,
TreeListSelectionChangeEvent,
TreeListExpandChangeEvent,
TreeListFilterChangeEvent,
TreeListTextFilter,createDataTree, 
TREELIST_COL_INDEX_ATTRIBUTE,
TreeListSortChangeEvent, orderBy} from '@progress/kendo-react-treelist';
import { getter } from "@progress/kendo-data-query";
import { useTableKeyboardNavigation } from "@progress/kendo-react-data-tools";
import { SvgIcon } from "@progress/kendo-react-common";
import { classNames } from '@progress/kendo-react-common';
import { blurIcon, globeIcon, gearIcon, folderMoreIcon, questionCircleIcon, chevronDownIcon, chevronRightIcon } from "@progress/kendo-svg-icons";
import { GetEndpointIcon, GetEndpointMP2Icon, GetBulkFlowIcon, GetPointIcon, GetHeadUnitIcon, GetGatewayIcon, GetGatewayHMEIcon } from "../../../Utilities/CustomIcons";
import { DeviceType } from "../../../Enums/DeviceType";

interface TreeViewDataItem {
    treeListData: Array<TreeListReadDto>
    selectedState: any
    expandState: any
    onSelectionChange: (selectedItem: TreeListReadDto, selectedState: any) => void
    onExpandChange: (expandState: any) => void
}

function GetReservoirIcon (node: TreeListReadDto)
{
  switch (node.type)
  {
    case "Point":
    case "Endpoint":
    case DeviceType.EndpointMP2:
    case DeviceType.EndpointBF:
    case DeviceType.EndpointLF:
    case DeviceType.EndpointHF:

      if (node.reservoirId !== "")
      {
        return <SvgIcon icon={blurIcon} style={{width: '16px', height: '16px'}} />
      }
      else
      {
        return "";
      }

    default: 
      return "";
  }
}

function GetTreeNodeIcon (type: string)
{
    switch(type)
    {
      case "Location":
        return <SvgIcon icon={globeIcon} style={{width: '16px', height: '16px'}} />


      case "Asset":
        return <SvgIcon icon={gearIcon} style={{width: '16px', height: '16px'}} />

      case "Point":

        return GetPointIcon(14,14)
                
      case "Endpoint":
      case DeviceType.EndpointLF:
      case DeviceType.EndpointHF:
        return GetEndpointIcon(18,18)
      
      case DeviceType.EndpointMP2:
        return GetEndpointMP2Icon(18,18)

      case DeviceType.EndpointBF:
        return GetBulkFlowIcon(18,18)

      case DeviceType.HeadUnit:
        return GetHeadUnitIcon(18,18)

      case DeviceType.Gateway:
        return GetGatewayIcon(18,18)

      case DeviceType.GatewayHME:
        return GetGatewayHMEIcon(18,18)

      case "Points":
      case "Endpoints":
        return <SvgIcon icon={folderMoreIcon} style={{width: '16px', height: '16px'}} />
  
      default:
        return <SvgIcon icon={questionCircleIcon} style={{width: '16px', height: '16px'}} />
    }
  }

const iconContainerStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '26px',
  height: '26px',
  verticalAlign: 'middle'
};

const clickableIconStyle = {
  ...iconContainerStyle,
  cursor: 'pointer'
};

const TreeCell = (props: any) => {

  const field = props.field || "";
  const value = props.dataItem[field];
  const navigationAttributes = useTableKeyboardNavigation(props.id);
  const {isSelected } = props;
  const icons = [] as any[];
  
  if (props.expandable) {
    icons.push(
      ...props.level
        .slice(1)
        .map((_x: any, i: React.Key | null | undefined) => (
          <span 
            style={iconContainerStyle}
            key={i} 
          />
        ))
    );

    if (props.hasChildren) {
      icons.push(
        <span
          style={clickableIconStyle}
          key="expand-collapse"
          onClick={(event) => props.onExpandChange(event, props.dataItem, props.level)}
          {...{ [TREELIST_COL_INDEX_ATTRIBUTE]: props.colIndex }}
        >
          <SvgIcon icon={props.expanded ? chevronDownIcon : chevronRightIcon} />
        </span>
      );
    } else {
      icons.push(
        <span 
          style={iconContainerStyle}
          key={icons.length} 
        />
      );
    }
  }

  return (
    <td 
      aria-selected={props.isSelected} 
      style={{ whiteSpace: 'nowrap' }}
      className={classNames(
        'k-table-td',
        props.className,
        {
            'k-text-nowrap': props.expandable,
            'k-selected': isSelected
        })}
      {...navigationAttributes}
      {...{[TREELIST_COL_INDEX_ATTRIBUTE]: props.colIndex }}
    >
      {icons}
      {GetTreeNodeIcon(props.dataItem.type)}
      {" "}
      {value === null ? "" : props.dataItem[field].toString()}
      {" "}
      {GetReservoirIcon(props.dataItem)}
    </td>
  );
};

export default function TreeListComponent(props: TreeViewDataItem): ReactElement {

  const DATA_ITEM_KEY: string = 'id';
  const SUB_ITEMS_FIELD: string = 'treeListData';
  const EXPAND_FIELD: string = 'expanded';
  const SELECTED_FIELD: string = 'selected';
  const idGetter = getter(DATA_ITEM_KEY);

  const dataTree = createDataTree(
    props.treeListData,
    (i) => i.id,
    (i) => i.parentId,
    SUB_ITEMS_FIELD
  );

  const extendData: (
    dataState: any[],
    selectedState: { [id: string]: boolean },
    expandedState: { [n: number]: boolean }
  ) => any[] = (dataState, selectedState, expandedState) => {
    return mapTree(dataState, SUB_ITEMS_FIELD, (item) =>
      extendDataItem(item, SUB_ITEMS_FIELD, {
        selected: selectedState[idGetter(item)],
        expanded: expandedState[idGetter(item)],
      })
    );
  };

   useEffect(() => {
    setSelectedState(props.selectedState);
   }, [props.selectedState]); 

   useEffect(() => {
    setExpandedState(props.expandState);
   }, [props.expandState]); 

  const [selectedState, setSelectedState] = React.useState(props.selectedState);

  const [expandedState, setExpandedState] = React.useState<{
    [n: number]: boolean;
  }>(props.expandState);

  useEffect(() => {
    props.onExpandChange(expandedState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [expandedState]);

   const [dataState, setDataState] = useState<any>({
    data: [...dataTree],
    filter: [],
    expanded: [],
    sort: [{ field: "name", dir: "asc" }],

  });

  const onExpandChange = React.useCallback(
    (e: TreeListExpandChangeEvent) => {
      setExpandedState({ ...expandedState, [idGetter(e.dataItem)]: !e.value });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expandedState]
  );

  const onSelectionChange = React.useCallback(
    (event: TreeListSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: DATA_ITEM_KEY,
      });
      setSelectedState(newSelectedState);
    
      var selectedItem = props.treeListData.filter((item) => item.id === Object.keys(newSelectedState)[0])
      props.onSelectionChange(selectedItem[0], newSelectedState)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedState]
  );

  const handleFilterChange = (event: TreeListFilterChangeEvent) => {
    setDataState({
      ...dataState,
      filter: event.filter,
    });
  };

  const addExpandField = (dataTree) => {
    const expanded = dataState.expanded;
    return mapTree(dataTree, SUB_ITEMS_FIELD, (item) =>
      extendDataItem(item, SUB_ITEMS_FIELD, {
        [EXPAND_FIELD]: expanded.includes(item.id),
      })
    );
  };

  const processData = () => {
    const { data, filter, sort } = dataState;

    const dataTree = orderBy(
      filterBy(data, filter, SUB_ITEMS_FIELD),
      sort,
      SUB_ITEMS_FIELD
    );
    return addExpandField(dataTree);
  };

  const onSortChange = (event: TreeListSortChangeEvent) => {
    setDataState({
      ...dataState,
      sort: event.sort,
    });
  };

  return (
    <div style={{height: '100%',}} className={"extra-filter-button"}>
        <TreeList
            data={extendData(processData(), selectedState, expandedState)}
            selectedField={SELECTED_FIELD}
            expandField={EXPAND_FIELD}
            subItemsField={SUB_ITEMS_FIELD}
            dataItemKey={DATA_ITEM_KEY}
            onExpandChange={onExpandChange}
            onSelectionChange={onSelectionChange}
            onFilterChange={handleFilterChange}
            onSortChange={onSortChange}
            filter={dataState.filter}
            sort={dataState.sort}
            sortable={true}
            selectable={{
              enabled: true,
              mode: 'single',
            }}
            navigatable={true}
            columns={[
            {
                field: 'name',
                title: 'Site Explorer',
                expandable: true,
                filterCell: TreeListTextFilter,
                cell: TreeCell
            },
            ]}
        />
    </div>
  )
}
